import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, featuredContent } from '@/mocks/content';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  const featuredItem = featuredContent.find((f) => f.id === slug);

  if (!article && !featuredItem) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="text-xl font-heading text-foreground-900 mb-3">内容未找到</h2>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (featuredItem && !article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${featuredItem.categorySlug}`} className="hover:text-primary-600 transition-colors">{featuredItem.category}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">{featuredItem.title}</span>
            </nav>

            <div className="relative w-full h-64 md:h-96 rounded-xl overflow-hidden mb-8">
              <img
                src={featuredItem.image}
                alt={featuredItem.title}
                className="w-full h-full object-cover object-top"
              />
              {featuredItem.badge && (
                <span className="absolute top-4 left-4 px-3 py-1 text-sm font-semibold rounded-full bg-primary-500 text-background-50 whitespace-nowrap">
                  {featuredItem.badge}
                </span>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4">{featuredItem.title}</h1>
            <div className="flex items-center gap-4 text-sm text-foreground-400 mb-8 flex-wrap">
              <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs whitespace-nowrap">{featuredItem.category}</span>
              <span className="flex items-center gap-1"><i className="ri-star-fill text-primary-500"></i> {featuredItem.rating}</span>
              <span>{featuredItem.views} 次播放</span>
            </div>

            <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
              <p>该节目正在 bytv 平台热播中，登录 bytv 即可观看完整内容。</p>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!article) return null;

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://8cc6u.cn' },
          { '@type': 'ListItem', position: 2, name: article.category, item: `https://8cc6u.cn/category/${article.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        datePublished: article.date,
        author: { '@type': 'Person', name: article.author },
        image: article.image,
        publisher: {
          '@type': 'Organization',
          name: 'bytv',
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main className="min-h-screen pt-16 bg-background-50">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors">{article.category}</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
          </nav>

          <div className="relative w-full h-64 md:h-96 rounded-xl overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <header className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4 leading-snug">{article.title}</h1>
            <div className="flex items-center gap-4 text-sm text-foreground-400 flex-wrap">
              <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs whitespace-nowrap">{article.category}</span>
              <span className="flex items-center gap-1"><i className="ri-user-line"></i> {article.author}</span>
              <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {article.date}</span>
              <span className="flex items-center gap-1"><i className="ri-eye-line"></i> {article.views} 阅读</span>
              <span className="flex items-center gap-1"><i className="ri-time-line"></i> 阅读约{article.readTime}</span>
            </div>
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>
          </header>

          <div
            className="prose prose-sm max-w-none text-foreground-700 leading-relaxed
              prose-h2:text-lg prose-h2:font-heading prose-h2:text-foreground-900 prose-h2:mt-8 prose-h2:mb-4
              prose-p:mb-4 prose-p:leading-relaxed
              [&_h2]:text-lg [&_h2]:font-heading [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4
              [&_p]:mb-4 [&_p]:leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {relatedArticles.length > 0 && (
            <section className="mt-16 pt-10 border-t border-background-200">
              <h3 className="text-lg font-heading text-foreground-900 mb-6">相关推荐</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group cursor-pointer bg-background-100 rounded-xl overflow-hidden hover:shadow-sm transition-all duration-300"
                  >
                    <div className="relative w-full h-36 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h4>
                      <span className="text-xs text-foreground-400 mt-1.5 block">{ra.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}